/**
 * CookieConsent — §10-G GDPR-grade cookie preference panel.
 *
 * Three categories (Essential always-on; Functional + Analytics opt-in).
 * Persists to localStorage and exposes the chosen state on window so
 * other modules (e.g. Sentry, analytics) can gate themselves.
 *
 * Storage key: `ek-cookie-consent` (JSON: { essential: true, functional, analytics, ts })
 * Event: `window.dispatchEvent(new CustomEvent('ek-cookie-consent-change', {detail}))`
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Cookie, X, Check, Shield } from "lucide-react";
import { loadClarity } from "../lib/consent";

const STORAGE_KEY = "ek-cookie-consent";
const CONSENT_VERSION = 1;

const CATEGORIES = [
  {
    id: "essential",
    label: "Essential",
    locked: true,
    description: "Required for the site to function: session, security, consent itself. Cannot be disabled.",
  },
  {
    id: "functional",
    label: "Functional",
    locked: false,
    description: "Remembers preferences (text-zoom, theme, dismissed banners). No tracking.",
  },
  {
    id: "analytics",
    label: "Analytics",
    locked: false,
    description: "Anonymised usage data via Sentry & analytics. Helps us fix bugs and improve.",
  },
];

const readConsent = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeConsent = (choice) => {
  const payload = {
    v: CONSENT_VERSION,
    essential: true,
    functional: !!choice.functional,
    analytics: !!choice.analytics,
    ts: new Date().toISOString(),
  };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {/*noop*/}
  try { window.dispatchEvent(new CustomEvent("ek-cookie-consent-change", { detail: payload })); } catch {/*noop*/}
  // Analytics consent gates cookie-based analytics (Microsoft Clarity). Plausible
  // is cookieless and always-on; Clarity only fires once the visitor opts in here.
  if (payload.analytics) { try { loadClarity(); } catch {/*noop*/} }
  return payload;
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(false);
  const bannerRef = useRef(null);

  // Publish the banner height so floating CTAs can sit above it (no overlap).
  useEffect(() => {
    const root = document.documentElement;
    if (visible && bannerRef.current) {
      root.style.setProperty("--ek-cookie-bar-h", `${bannerRef.current.offsetHeight}px`);
    } else {
      root.style.setProperty("--ek-cookie-bar-h", "0px");
    }
    return () => root.style.setProperty("--ek-cookie-bar-h", "0px");
  }, [visible, showDetail]);

  useEffect(() => {
    const existing = readConsent();
    // The consent banner is for public visitors; never auto-surface it inside
    // the admin console (operators are internal + it overlays admin panels).
    const onAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    if (!existing && !onAdmin) {
      setVisible(true);
    } else if (existing) {
      setFunctional(existing.functional);
      setAnalytics(existing.analytics);
      // Re-arm Clarity on load if the visitor previously opted into analytics.
      if (existing.analytics) { try { loadClarity(); } catch {/*noop*/} }
    }
    // Allow re-opening the panel from the footer "Cookie preferences" link.
    const openHandler = () => { setShowDetail(true); setVisible(true); };
    window.addEventListener("ek-open-cookie-consent", openHandler);
    return () => window.removeEventListener("ek-open-cookie-consent", openHandler);
  }, []);

  const acceptAll = useCallback(() => {
    writeConsent({ functional: true, analytics: true });
    setVisible(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    writeConsent({ functional: false, analytics: false });
    setVisible(false);
  }, []);

  const saveChoices = useCallback(() => {
    writeConsent({ functional, analytics });
    setVisible(false);
  }, [functional, analytics]);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed inset-x-0 bottom-0 z-[70] bg-black text-white border-t border-white/20 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.6)]"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent-banner"
    >
      <div className="ek-container py-6 md:py-7">
        <div className="flex items-start gap-4">
          <Cookie size={22} className="text-[#A179FA] shrink-0 mt-1" />
          <div className="flex-1">
            <h2 id="cookie-consent-title" className="ek-heading text-base md:text-lg">
              Cookie preferences
            </h2>
            <p className="text-zinc-300 text-[13px] mt-1 leading-relaxed max-w-2xl">
              We use cookies to make this site work and to understand how it&rsquo;s
              used. Essential cookies are always on. Choose what else you&rsquo;re
              comfortable with — you can change this any time from the footer link.
            </p>

            {showDetail && (
              <div
                className="mt-4 border border-white/15 divide-y divide-white/10"
                data-testid="cookie-consent-detail"
              >
                {CATEGORIES.map((cat) => {
                  const checked =
                    cat.id === "essential" ? true :
                    cat.id === "functional" ? functional :
                    analytics;
                  const onToggle = (e) => {
                    if (cat.locked) return;
                    if (cat.id === "functional") setFunctional(e.target.checked);
                    if (cat.id === "analytics") setAnalytics(e.target.checked);
                  };
                  return (
                    <label
                      key={cat.id}
                      className="flex items-start gap-3 p-4 cursor-pointer"
                      data-testid={`cookie-consent-cat-${cat.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={onToggle}
                        disabled={cat.locked}
                        className="mt-1 accent-[#7737F6]"
                        data-testid={`cookie-consent-toggle-${cat.id}`}
                      />
                      <div className="flex-1">
                        <div className="text-[12.5px] tracking-[0.14em] uppercase text-white inline-flex items-center gap-2">
                          {cat.label}
                          {cat.locked && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase text-zinc-400">
                              <Shield size={10} /> Always on
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-400 text-[12px] mt-1 normal-case tracking-normal">{cat.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <button
                type="button"
                onClick={acceptAll}
                className="ek-btn bg-[#7737F6] text-white text-[11.5px] tracking-[0.16em] uppercase px-5 py-2.5 inline-flex items-center gap-1.5 hover:opacity-90"
                data-testid="cookie-consent-accept-all"
              >
                <Check size={12} /> Accept all
              </button>
              <button
                type="button"
                onClick={rejectNonEssential}
                className="border border-white/40 text-white text-[11.5px] tracking-[0.16em] uppercase px-5 py-2.5 inline-flex items-center gap-1.5 hover:border-white"
                data-testid="cookie-consent-reject"
              >
                Reject non-essential
              </button>
              {showDetail ? (
                <button
                  type="button"
                  onClick={saveChoices}
                  className="border border-white/40 text-white text-[11.5px] tracking-[0.16em] uppercase px-5 py-2.5 hover:border-white"
                  data-testid="cookie-consent-save"
                >
                  Save choices
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDetail(true)}
                  className="text-[11.5px] tracking-[0.16em] uppercase text-[#A179FA] hover:text-white"
                  data-testid="cookie-consent-customise"
                >
                  Customise →
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={rejectNonEssential}
            className="text-zinc-400 hover:text-white shrink-0"
            aria-label="Dismiss (reject non-essential)"
            data-testid="cookie-consent-dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper for other modules to gate themselves on consent
export const isAnalyticsConsented = () => {
  const c = readConsent();
  return !!c?.analytics;
};

// Helper for the footer link to re-open the preferences panel at any time.
export const openCookiePreferences = () => {
  try { window.dispatchEvent(new CustomEvent("ek-open-cookie-consent")); } catch {/*noop*/}
};
